import { observer } from 'mobx-react'
import React from 'react'
import { JobProgressionStepStatus } from 'jobs/progression/JobProgression'
import DraftInReviewProgressionActions from './DraftInReview'
import NoProgressionActions from './NoProgression'
import JobStartedProgressionActions from './JobStarted'
import OpenItemsProgressionActions from './OpenItems'
import DraftRejectedProgressionActions from './DraftRejected'
import DraftApprovedProgressionActions from './DraftApproved'
import AuthorizationSignedProgressionActions from './AuthorizationSigned'
import AuthorizationRequestedProgressionActions from './AuthorizationRequested'
import DeliverableCompletedProgressionActions from './DeliverableCompleted'
import CompleteJobProgressionActions from './PreparationComplete'
import ReturnFiledProgressionActions from './ReturnFiled'
import {
  Card,
  CardContent,
  CardContentDescription,
  CardHeader,
  CardHeaderActions,
  CardTitle,
} from 'components/Card'
import { IconButton } from 'components/IconButton'
import Icon from 'components/Icon'
import {
  Dropdown,
  DropdownItem,
  DropdownItemTitle,
  DropdownTitle,
} from 'components/Dropdown'
import ProgressBar from 'components/ProgressBar'
import { SectionError, SectionLoading } from '../Section/Section'
import JobViewModel from '../JobViewModel'
import JobProgressionViewModel from './JobProgressionViewModel'
import JobExtensionProgressionNotStarted from './JobExtensionProgressionNotStarted'
import JobExtensionInProgressProgressionActions from './JobExtensionInProgress'
import JobExtensionConfirmedProgressionActions from './JobExtensionConfirmed'
import JobExtensionCompletedProgressionActions from './JobExtensionCompleted'
import WaitingOnClientProgressionActions from './WaitingOnClient'
import WaitingOnClientProgressionProActions from './WaitingOnClientProActions'
import DraftInReviewProActions from './DraftInReviewProActions'
import WaitingOnAdminProgressionActions from './WaitOnAdminActions'
import { markdownToHtml } from 'utils/markdown-util'

interface Props {
  vm: JobViewModel
}

export default observer(function JobProgressionCard({ vm }: Props) {
  return (
    <Card>
      <JobProgressionCardContent vm={vm} />
    </Card>
  )
})

export const JobProgressionCardContent = observer(
  function JobProgressionCardContent({ vm }: Props) {
    const jobProgressionVm = vm.jobProgressionVM
    const jobIsCompleted = vm.job.completed
    const currentStepInfo = jobProgressionVm.currentStepInfo

    return jobProgressionVm.activate.match({
      pending: () => <SectionLoading />,
      rejected: (err) => (
        <SectionError
          title="An error occurred while loading the job progression."
          error={err}
        />
      ),
      resolved: () => (
        <>
          <CardHeader>
            <CardTitle dataTest="job-progression-card-title">
              {jobIsCompleted ? 'Job completed' : currentStepInfo?.title}
            </CardTitle>
            {!!jobProgressionVm.progressionSteps?.length && (
              <CardHeaderActions>
                <Dropdown
                  autoDismiss
                  dataTest="job-progression-card-dropdown-menu"
                  button={
                    <IconButton>
                      <Icon name="more" rotate={90} />
                    </IconButton>
                  }
                >
                  <DropdownTitle>Actions</DropdownTitle>
                  <DropdownItem
                    icon={<Icon name="clock" />}
                    onClick={jobProgressionVm.historyDialogVM.show}
                    data-test="job-progression-view-history"
                  >
                    <DropdownItemTitle>View history</DropdownItemTitle>
                  </DropdownItem>
                </Dropdown>
              </CardHeaderActions>
            )}
          </CardHeader>
          <CardContent>
            <ProgressBar
              key={1}
              bars={[
                {
                  progress: jobProgressionVm.completionStatus,
                },
                {
                  progress: jobProgressionVm.pendingCompletionStatus,
                  style: 'striped',
                },
              ]}
            />
          </CardContent>

          {vm.isProviderCollaborator && (
            <>
              <CardContent>
                <CardContentDescription
                  dangerouslySetInnerHTML={{
                    __html: markdownToHtml(
                      jobIsCompleted
                        ? 'Congratulations! There are no more tasks to complete.'
                        : (currentStepInfo?.proDescription ?? ''),
                    ),
                  }}
                ></CardContentDescription>
              </CardContent>
              {!jobIsCompleted && (
                <ProviderProgressionActions
                  vm={jobProgressionVm}
                  jobVM={vm}
                  canCompleteJob={vm.canCompleteJobProgression}
                />
              )}
            </>
          )}
          {vm.isChampionClientCollaborator && (
            <>
              <CardContent>
                <CardContentDescription
                  dangerouslySetInnerHTML={{
                    __html: markdownToHtml(
                      jobIsCompleted
                        ? 'Congratulations! There are no more tasks to complete.'
                        : (currentStepInfo?.clientDescription ?? ''),
                    ),
                  }}
                ></CardContentDescription>
              </CardContent>
              {!jobIsCompleted && <ClientProgressionActions vm={vm} />}
            </>
          )}
          {!jobIsCompleted && vm.canSetReadyToFile && (
            <>
              {!vm.isProviderCollaborator &&
                !vm.isChampionClientCollaborator && (
                  <CardContent>
                    <CardContentDescription
                      dangerouslySetInnerHTML={{
                        __html: markdownToHtml(
                          jobIsCompleted
                            ? 'Congratulations! There are no more tasks to complete.'
                            : (currentStepInfo?.proDescription ?? ''),
                        ),
                      }}
                    />
                  </CardContent>
                )}
              <AdminProgressionActions vm={vm} />
            </>
          )}

          {vm.canApproveDraftAsSupportingClient && (
            <>
              <CardContent>
                <CardContentDescription
                  dangerouslySetInnerHTML={{
                    __html: markdownToHtml(
                      jobIsCompleted
                        ? 'Congratulations! There are no more tasks to complete.'
                        : (currentStepInfo?.clientDescription ?? ''),
                    ),
                  }}
                />
              </CardContent>
              <SupportingClientProgressionActions vm={vm} />
            </>
          )}
        </>
      ),
    })
  },
)

interface SupportingClientProgressionActionsProps {
  vm: JobViewModel
}

const SupportingClientProgressionActions = observer(
  ({ vm }: SupportingClientProgressionActionsProps) => {
    switch (vm.jobProgressionVM.currentStepStatus) {
      case JobProgressionStepStatus.DRAFT_IN_REVIEW:
        return (
          <DraftInReviewProgressionActions
            showApproveDraftDialog={vm.approveDraftInReviewDialogVM.show}
            showRejectDraftDialog={vm.rejectDraftInReviewDialogVM.show}
          />
        )
      default:
        return <></>
    }
  },
)

interface ClientProgressionActionsProps {
  vm: JobViewModel
}

const ClientProgressionActions = observer(function ClientProgressionActions({
  vm,
}: ClientProgressionActionsProps) {
  const jobProgressionVm = vm.jobProgressionVM

  switch (jobProgressionVm.currentStepStatus) {
    case JobProgressionStepStatus.DRAFT_IN_REVIEW:
      return (
        <DraftInReviewProgressionActions
          showApproveDraftDialog={vm.approveDraftInReviewDialogVM.show}
          showRejectDraftDialog={vm.rejectDraftInReviewDialogVM.show}
        />
      )
    case JobProgressionStepStatus.PROGRESSION_WAITING_ON_CLIENT:
      return <WaitingOnClientProgressionActions vm={jobProgressionVm} />
    default:
      return <></>
  }
})

interface AdminProgressionActionsProps {
  vm: JobViewModel
}

const AdminProgressionActions = observer(function AdminProgressionActions({
  vm,
}: AdminProgressionActionsProps) {
  const jobProgressionVm = vm.jobProgressionVM

  switch (jobProgressionVm.currentStepStatus) {
    case JobProgressionStepStatus.PROGRESSION_WAITING_ON_CLIENT:
      return <WaitingOnAdminProgressionActions vm={jobProgressionVm} />
    case JobProgressionStepStatus.DRAFT_IN_REVIEW:
      // Ensure we don't show this action because the champion client
      // and the supporting client will have their own actions
      return (
        !vm.isChampionClientCollaborator &&
        !vm.canApproveDraftAsSupportingClient && (
          <DraftInReviewProgressionActions
            showApproveDraftDialog={vm.approveDraftInReviewDialogVM.show}
            showRejectDraftDialog={vm.rejectDraftInReviewDialogVM.show}
          />
        )
      )
    default:
      return <></>
  }
})

interface ProviderProgressionActionsProps {
  vm: JobProgressionViewModel
  jobVM: JobViewModel
  canCompleteJob: boolean
}

const ProviderProgressionActions = observer(
  function ProviderProgressionActions({
    vm,
    jobVM,
    canCompleteJob,
  }: ProviderProgressionActionsProps) {
    switch (vm.currentStepStatus) {
      case JobProgressionStepStatus.OPEN_ITEMS:
        return <OpenItemsProgressionActions vm={vm} />
      case JobProgressionStepStatus.JOB_STARTED:
        return <JobStartedProgressionActions vm={vm} />
      case JobProgressionStepStatus.DRAFT_IN_REVIEW:
        return <DraftInReviewProActions vm={vm} />
      case JobProgressionStepStatus.DRAFT_REJECTED:
        return <DraftRejectedProgressionActions vm={vm} />
      case JobProgressionStepStatus.DRAFT_APPROVED:
        return (
          <DraftApprovedProgressionActions
            vm={vm}
            jobVM={jobVM}
            canCompleteJob={canCompleteJob}
          />
        )
      case JobProgressionStepStatus.AUTHORIZATION_REQUESTED:
        return <AuthorizationRequestedProgressionActions vm={vm} />
      case JobProgressionStepStatus.AUTHORIZATION_SIGNED:
        return <AuthorizationSignedProgressionActions vm={vm} />
      case JobProgressionStepStatus.RETURN_FILED:
        return (
          <ReturnFiledProgressionActions
            vm={vm}
            jobVM={jobVM}
            canCompleteJob={canCompleteJob}
          />
        )
      case JobProgressionStepStatus.DELIVERABLE_COMPLETED:
        return (
          <DeliverableCompletedProgressionActions
            vm={vm}
            canCompleteJob={canCompleteJob}
          />
        )
      case JobProgressionStepStatus.NOT_STARTED:
        return <NoProgressionActions vm={vm} />

      case JobProgressionStepStatus.EXTENSION_NOT_STARTED:
        return <JobExtensionProgressionNotStarted vm={vm} />

      case JobProgressionStepStatus.EXTENSION_IN_PROGRESS:
        return <JobExtensionInProgressProgressionActions vm={vm} />

      case JobProgressionStepStatus.EXTENSION_CONFIRMED:
        return <JobExtensionConfirmedProgressionActions vm={vm} />

      case JobProgressionStepStatus.EXTENSION_ACCEPTED:
      case JobProgressionStepStatus.EXTENSION_REJECTED:
      case JobProgressionStepStatus.READY_TO_FILE:
        return (
          <JobExtensionCompletedProgressionActions
            vm={vm}
            canCompleteJob={canCompleteJob}
          />
        )

      case JobProgressionStepStatus.PROGRESSION_WAITING_ON_CLIENT:
        return <WaitingOnClientProgressionProActions vm={vm} />

      case JobProgressionStepStatus.PREPARATION_COMPLETED:
        return <CompleteJobProgressionActions vm={vm} />
      case JobProgressionStepStatus.REVIEW_COMPLETED:
        return <CompleteJobProgressionActions vm={vm} />

      default:
        return <></>
    }
  },
)
